import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, annotationPlugin);

// Styled Components
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 80vh;               /* adjust/replace if your card sets its own height */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  padding: 20px;
  position: relative;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
  justify-content: center;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover { color: #000; }

  svg { width: 18px; height: 18px; }
`;

const monthNames = [
  { value: "ALL", label: "Cały rok" },
  { value: 1, label: "Styczeń" },
  { value: 2, label: "Luty" },
  { value: 3, label: "Marzec" },
  { value: 4, label: "Kwiecień" },
  { value: 5, label: "Maj" },
  { value: 6, label: "Czerwiec" },
  { value: 7, label: "Lipiec" },
  { value: 8, label: "Sierpień" },
  { value: 9, label: "Wrzesień" },
  { value: 10, label: "Październik" },
  { value: 11, label: "Listopad" },
  { value: 12, label: "Grudzień" },
];

// === Standard palette ===
const GREEN  = "#2e7d32"; // current-year bars / trend
const ORANGE = "#fb8c00"; // previous-year bars / trend
const BLUE   = "#1976d2"; // average/reference line

const HistogramChart = ({ data }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("ALL");
  const [chartData, setChartData] = useState(null);
  const [groupedData, setGroupedData] = useState(null);
  const [globalAvg, setGlobalAvg] = useState(0);

  const calculateTransactionValue = (row) =>
    parseFloat(
      row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22
    ) || 0;

  // Group source once (year -> month -> contractor -> {total, count})
  useEffect(() => {
    if (!data || data.length === 0) return;

    const grouped = data.reduce((acc, row) => {
      const year = parseInt(row.Rok, 10);
      const month = parseInt(row.Miesiac, 10);
      const contractor = row.NazwaKontrahenta || "Nieznany";
      const transactionValue = calculateTransactionValue(row);

      if (!acc[year]) acc[year] = {};
      if (!acc[year][month]) acc[year][month] = {};
      if (!acc[year][month][contractor]) acc[year][month][contractor] = { total: 0, count: 0 };

      acc[year][month][contractor].total += transactionValue;
      acc[year][month][contractor].count += 1;
      return acc;
    }, {});

    const years = Object.keys(grouped).map((y) => parseInt(y, 10)).sort((a, b) => b - a);
    setAvailableYears(years);
    setSelectedYear((prev) => prev || years[0]);
    setSelectedMonth("ALL");
    setGroupedData(grouped);
  }, [data]);

  // Build chart for current selections
  useEffect(() => {
    if (!selectedYear || !groupedData || !groupedData[selectedYear]) return;

    // Helper: collect contractor totals+counts for a given year and month filter
    const collect = (year, monthSel) => {
      const yearData = groupedData[year] || {};
      if (monthSel === "ALL") {
        const acc = {};
        Object.values(yearData).forEach((monthData) => {
          for (const [contractor, stats] of Object.entries(monthData)) {
            if (!acc[contractor]) acc[contractor] = { total: 0, count: 0 };
            acc[contractor].total += stats.total;
            acc[contractor].count += stats.count;
          }
        });
        return acc;
      } else {
        return yearData[monthSel] || {};
      }
    };

    const currMap = collect(selectedYear, selectedMonth);
    const prevMap = collect(selectedYear - 1, selectedMonth);

    // current period averages
    const currAverages = Object.entries(currMap).map(([name, stats]) => ({
      name,
      avg: stats.count ? stats.total / stats.count : 0,
    }));

    // Top 10 by current period
    const top10 = currAverages.sort((a, b) => b.avg - a.avg).slice(0, 10);

    const labels = top10.map((c) => (c.name.length > 15 ? c.name.slice(0, 15) + "…" : c.name));
    const currVals = top10.map((c) => c.avg);

    // previous period averages aligned to labels
    const prevVals = top10.map((c) => {
      const s = prevMap[c.name];
      return s && s.count ? s.total / s.count : 0;
    });

    // global average (current period only)
    const totalSum = currVals.reduce((s, v) => s + v, 0);
    const global = currVals.length ? totalSum / currVals.length : 0;
    setGlobalAvg(global);

    const datasetCurrent = {
      label: `Średnia ${selectedYear}`,
      data: currVals,
      backgroundColor: "rgba(46, 125, 50, 0.70)",  // uniform green
      borderColor: GREEN,
      borderWidth: 1,
    };

    const datasetPrev = {
      label: `Średnia ${selectedYear - 1}`,
      data: prevVals,
      backgroundColor: "rgba(251, 140, 0, 0.60)",
      borderColor: ORANGE,
      borderWidth: 1,
    };

    setChartData({
      labels,
      datasets: [datasetCurrent, datasetPrev],
    });
  }, [selectedYear, selectedMonth, groupedData]);

  if (!data || data.length === 0) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="avg-transaction-info">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="avg-transaction-info"
        place="left"
        content={
          <>
            <strong>Średnia wartość transakcji</strong>
            <br />
            Top 10 kontrahentów wg średniej (bieżący okres)
            <br />+ porównanie do poprzedniego roku.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:&nbsp;</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="month-select">Miesiąc:&nbsp;</label>
          <select
            id="month-select"
            value={selectedMonth}
            onChange={(e) =>
              setSelectedMonth(e.target.value === "ALL" ? "ALL" : parseInt(e.target.value, 10))
            }
          >
            {monthNames.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      {chartData && (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: "Średnia wartość transakcji" },
                ticks: {
                  callback: (v) =>
                    Number(v).toLocaleString("pl-PL", { maximumFractionDigits: 0 }),
                },
              },
              x: { title: { display: true, text: "Kontrahenci (Top 10 wg bieżącego okresu)" } },
            },
            plugins: {
              legend: { position: "top" },
              title: {
                display: true,
                text: `Top 10 kontrahentów – Średnia wartość (${selectedYear} vs ${selectedYear - 1}, ${
                  selectedMonth === "ALL"
                    ? "Cały rok"
                    : monthNames.find((m) => m.value === selectedMonth)?.label
                })`,
              },
              tooltip: {
                mode: "index",
                intersect: false,
                callbacks: {
                  label: (ctx) => {
                    const val = ctx.parsed?.y ?? 0;
                    return `${ctx.dataset.label}: ${val.toLocaleString("pl-PL", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`;
                  },
                },
              },
              // Average (current period) line = BLUE
              annotation: {
                annotations: {
                  avgLine: {
                    type: "line",
                    yMin: globalAvg,
                    yMax: globalAvg,
                    borderColor: BLUE,
                    borderWidth: 2,
                    borderDash: [6, 6],
                    label: {
                      enabled: true,
                      content: `Średnia: ${globalAvg.toLocaleString("pl-PL")}`,
                      backgroundColor: "rgba(25, 118, 210, 0.08)",
                      color: BLUE,
                      position: "end",
                    },
                  },
                },
              },
            },
            interaction: { mode: "index", intersect: false },
          }}
        />
      )}
    </ChartWrapper>
  );
};

export default HistogramChart;
